/**
 * "What are you planning?" — the door every new plan comes through.
 *
 * One click is the whole decision: the kind picks the starter room, the title
 * and the words, and everything it picks can be changed afterwards. So there is
 * no form here and no confirm button, only four rooms to choose between, each
 * drawn as it will open. Weddings first, because that is who came first.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef } from 'react'

import { EVENT_KINDS } from '../lib/events'
import { useT } from '../lib/i18n/I18nProvider'
import type { EventKind } from '../lib/types'
import { RoomThumb } from './RoomThumb'

interface Props {
  open: boolean
  onClose: () => void
  onChoose: (kind: EventKind) => void
  /** The kind being created, while it is; every choice waits meanwhile. */
  creating: EventKind | null
  error: string | null
}

export function NewPlanDialog({ open, onClose, onChoose, creating, error }: Props) {
  const t = useT()
  const firstChoice = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    // Keyboard users land on the first room, not on the page behind the dialog.
    const focus = requestAnimationFrame(() => firstChoice.current?.focus())
    return () => {
      window.removeEventListener('keydown', onKey)
      cancelAnimationFrame(focus)
    }
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.14 }}
          onClick={onClose}
          role="presentation"
        >
          <motion.div
            className="modal modal--settings modal--events"
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 6 }}
            transition={{ type: 'spring', duration: 0.34, bounce: 0.12 }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-plan-title"
          >
            <header className="modal-head">
              <div>
                <h2 id="new-plan-title" className="modal-title">
                  {t.events.chooseTitle}
                </h2>
                <p className="modal-lede">{t.events.chooseLede}</p>
              </div>
              <button
                type="button"
                className="btn btn-chrome btn-icon"
                onClick={onClose}
                aria-label={t.common.close}
              >
                ✕
              </button>
            </header>

            <div className="modal-body">
              <ul className="event-choices">
                {EVENT_KINDS.map((kind, index) => {
                  const words = t.events.kinds[kind]
                  return (
                    <li key={kind}>
                      <button
                        ref={index === 0 ? firstChoice : undefined}
                        type="button"
                        className={`event-choice event-choice--${kind}${
                          creating === kind ? ' is-busy' : ''
                        }`}
                        data-kind={kind}
                        onClick={() => onChoose(kind)}
                        disabled={creating !== null}
                      >
                        <span className="event-choice-room">
                          <RoomThumb kind={kind} />
                        </span>
                        <span className="event-choice-text">
                          <strong>{creating === kind ? t.dashboard.creating : words.name}</strong>
                          <small>{words.blurb}</small>
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
              {error && <p className="modal-error">{error}</p>}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
